import { channelMetaDataKeys } from '@inbox-demo-nx/constants';
import {
  useAuthContext,
  useScenarioCallbackContext,
  useScenarioContext,
  useSendApiRequest,
  useSendBirdContext,
} from '@inbox-demo-nx/contexts';
import {
  MessageContent,
  Reaction,
  ScenarioDataChannelState,
  ScenarioMessage,
  ScenarioStateContext,
  SingleContentScenarioMessage,
} from '@inbox-demo-nx/types';
import { useAnimatingCircleContext } from '@inbox-demo-nx/ui-animating-circle';
import { convertTimestamp, delay } from '@inbox-demo-nx/utils';
import {
  convertScenarioMessageToMessagePayload,
  processScenarioMessages,
} from '@inbox-demo-nx/utils-scenario';
import axios from 'axios';
import { useCallback, useMemo } from 'react';

export default function useScenarioUtils() {
  const sendApiRequest = useSendApiRequest();
  const scenario = useScenarioContext();
  const { currentUser } = useAuthContext();
  const { runCallback } = useScenarioCallbackContext();
  const { sendbird } = useSendBirdContext();
  const { setTargetId: setAnimatingCircleTarget } = useAnimatingCircleContext();

  const currentUserId = currentUser?.userId;

  const getLatestUserMetaData = useCallback(async () => {
    if (!currentUserId) return {};

    const userListQuery = sendbird.createApplicationUserListQuery();
    userListQuery.userIdsFilter = [currentUserId];
    const [latestUser] = await userListQuery.next();
    return latestUser.metaData;
  }, [currentUserId, sendbird]);

  return useMemo(() => {
    async function handleChannelStateTransition(
      userId: string,
      channelUrl: string,
      channelState: string
    ) {
      if (!currentUserId) {
        throw new Error('currentUser must exist.');
      }

      const channel = await sendbird.GroupChannel.getChannel(channelUrl);

      const scenarioData = await getScenarioDataFromCustomChannelType(
        channel.customType || ''
      );

      if (!scenarioData) {
        return;
      }

      const currentState = scenarioData.states[channelState];
      if (!currentState) {
        return;
      }

      const processedMessages = processScenarioMessages(currentState.messages);

      const sendMessage = async (message: typeof processedMessages[number]) => {
        const channelMetaData = await channel.getAllMetaData();

        const payload = convertScenarioMessageToMessagePayload(message, {
          myUserId: userId,
          channelMetaData,
          userMetaData: await getLatestUserMetaData(),
        });

        if (
          !('user_id' in payload) ||
          !payload.user_id ||
          payload.user_id === userId
        ) {
          return sendApiRequest({
            type: 'sendMessageAndTranslate',
            data: { channelUrl, params: payload },
          });
        } else {
          const isChannelMember = channel.members.some(
            (user) => user.userId === payload.user_id
          );
          if (!isChannelMember) {
            await sendApiRequest({
              type: 'inviteUser',
              data: { channelUrl, userId: payload.user_id },
            });
          }
          return sendApiRequest({
            type: 'typeAndSendMessage',
            data: {
              channelUrl,
              userId: payload.user_id,
              messageParams: payload,
              typingDuration: message.skipTyping ? 0 : 1000,
            },
          });
        }
      };

      const messageIds: number[] = [];
      for (const message of processedMessages) {
        const { message_id: messageId } = await sendMessage(message);
        await addReactionsToMessage({
          channelUrl,
          messageId,
          reactions: message.reactions,
        });
        messageIds.push(messageId);
      }

      if (currentState.onEntry) {
        await currentState.onEntry(
          await getScenarioStateContext({ userId, channelUrl, messageIds })
        );
      }
      if (currentState.after) {
        const { delay: delayMs, targetState } = currentState.after;
        delayMs && (await delay(delayMs));
        setChannelState({ channelUrl, state: targetState, userId });
      }
    }

    async function setChannelState({
      channelUrl,
      state,
      userId,
    }: {
      channelUrl: string;
      state: string;
      userId: string;
    }) {
      const channel = await sendbird.GroupChannel.getChannel(channelUrl);
      const channelMetaData = await channel.getMetaData([
        channelMetaDataKeys.state,
      ]);
      const previousState = channelMetaData[channelMetaDataKeys.state];

      if (previousState === String(state)) {
        return;
      }

      const channelScenario = getScenarioDataFromCustomChannelType(
        channel.customType || ''
      );
      const onExit = channelScenario?.states?.[previousState]?.onExit;
      if (onExit) {
        await onExit(await getScenarioStateContext({ userId, channelUrl }));
      }

      await channel.updateMetaData({ [channelMetaDataKeys.state]: state });
      handleChannelStateTransition(userId, channelUrl, String(state));
    }

    async function getScenarioStateContext({
      userId,
      channelUrl,
      messageIds,
    }: {
      userId: string;
      channelUrl: string;
      messageIds?: number[];
    }): Promise<ScenarioStateContext> {
      if (!currentUserId)
        throw new Error('Call getScenarioStateContext after logging in.');

      const channel = await sendbird.GroupChannel.getChannel(channelUrl);

      const scenarioStateContext: ScenarioStateContext = {
        currentUserId,
        channelUrl,
        messageIds,
        sendApiRequest,
        setChannelMetaData: (data) => channel.updateMetaData(data, true),
        runCallback,
        setAnimatingCircleTarget,
        transitionState: async (params) => {
          const getUpdateChannelStateRequest = async () => {
            if (typeof params === 'string') {
              return setChannelState({ channelUrl, state: params, userId });
            }

            const { channelUrl: targetChannelUrl } = await sendApiRequest({
              type: 'getChannelUrlByCustomType',
              data: {
                userId: currentUserId,
                customType: params.customChannelType,
              },
            });

            return setChannelState({
              channelUrl: targetChannelUrl,
              state: params.state,
              userId,
            });
          };

          try {
            await getUpdateChannelStateRequest();
          } catch (error) {
            if (axios.isAxiosError(error)) {
              console.error(error.toJSON());
            } else {
              console.error(error);
            }
          }
        },
        updateUserMessage: ({ channelUrl, messageId, payload }) =>
          sendApiRequest({
            type: 'editMessage',
            data: {
              channelUrl,
              messageId,
              params: { message_type: 'MESG', ...payload },
            },
          }),
        async sendMessage({ channelUrl, message }) {
          if (channelUrl) {
            const channel = await sendbird.GroupChannel.getChannel(channelUrl);
            const payload = convertScenarioMessageToMessagePayload(message, {
              myUserId: userId,
              channelMetaData: await channel.getAllMetaData(),
              userMetaData: await getLatestUserMetaData(),
            });
            await sendApiRequest({
              type: 'sendMessage',
              data: { channelUrl, params: payload },
            });
            return;
          }
          if (message.sender) {
            const [targetChannel] = await sendApiRequest({
              type: 'fetchGroupChannels',
              data: {
                userId,
                params: {
                  members_exactly_in: [message.sender, userId]
                    .map(encodeURIComponent)
                    .join(','),
                },
              },
            });
            const payload = convertScenarioMessageToMessagePayload(message, {
              myUserId: userId,
              channelMetaData: await sendApiRequest({
                type: 'getGroupChannelMetaData',
                data: { channelUrl: targetChannel.channel_url },
              }),
              userMetaData: currentUser.metaData,
            });
            await sendApiRequest({
              type: 'sendMessage',
              data: { channelUrl: targetChannel.channel_url, params: payload },
            });
            return;
          }
          throw new Error('Channel URL or sender is required');
        },
      };

      return scenarioStateContext;
    }

    function getScenarioDataFromCustomChannelType(customChannelType: string) {
      return scenario.channels.find((c) => c.customType === customChannelType);
    }

    async function addReactionsToMessage(data: {
      channelUrl: string;
      messageId: number;
      reactions?: Reaction[];
    }) {
      if ((data.reactions?.length ?? 0) > 0)
        return sendApiRequest({ type: 'addReactionsToMessage', data });
    }

    return {
      convertScenarioMessageToMessagePayload,
      getScenarioStateContext,
      getScenarioDataFromCustomChannelType,
      addReactionsToMessage,
      setChannelState,
      async handleChannelStateEvent<HandlerData>(
        handler:
          | ((data: HandlerData, context: ScenarioStateContext) => void)
          | ((context: ScenarioStateContext) => void)
          | undefined,
        params: HandlerData | undefined,
        channelContext: { channelUrl: string }
      ) {
        if (!handler || !currentUserId) {
          return;
        }
        const { channelUrl } = channelContext;
        const scenarioStateContext = await getScenarioStateContext({
          channelUrl,
          userId: currentUserId,
        });

        const args = params
          ? [params, scenarioStateContext]
          : [scenarioStateContext];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any

        try {
          const result = await (handler as any)(...args);
          return result;
        } catch (error) {
          if (axios.isAxiosError(error)) {
            console.error(error.toJSON());
          } else {
            console.error(error);
          }
        }
      },

      async getScenarioDataFromChannelUrl(channelUrl: string) {
        const channel = await sendbird.GroupChannel.getChannel(channelUrl);
        return getScenarioDataFromCustomChannelType(channel.customType || '');
      },

      convertMessagesToPayloadArray__TMP(
        messages: ScenarioDataChannelState['messages'] = []
      ) {
        return messages
          .flatMap((message) => {
            const singleContentMessages = (
              Array.isArray(message.content)
                ? message.content.map((content) => ({ ...message, content }))
                : [message]
            ) as (Omit<ScenarioMessage, 'content'> & {
              content: MessageContent;
            })[];
            return singleContentMessages;
          })
          .map(({ createdAt, ...rest }) => ({
            ...rest,
            createdAt: convertTimestamp(createdAt),
          }))
          .map((item, index, array) => {
            if (array[index - 1]?.createdAt === item.createdAt) {
              // If the consecutive messages have the same timestamp, we need to add a delay to the next message to keep the order.
              const { createdAt, ...rest } = item;
              return { ...rest, createdAt: createdAt + 1 };
            }
            return item;
          });
      },

      async requestSendMessage(
        message: SingleContentScenarioMessage,
        {
          myUserId,
          channelMetaData,
          channelUrl,
        }: {
          myUserId: string;
          channelMetaData: Record<string, string>;
          channelUrl: string;
        }
      ) {
        const userMetaData = await sendApiRequest({
          type: 'getAllUserMetaData',
          data: { userId: myUserId },
        });
        const sendMessagePayload = convertScenarioMessageToMessagePayload(
          message,
          { myUserId, channelMetaData, userMetaData }
        );
        const newMessage = await sendApiRequest({
          type: 'sendMessageAndTranslate',
          data: { channelUrl, params: sendMessagePayload },
        });
        await addReactionsToMessage({
          channelUrl,
          messageId: newMessage.message_id,
          reactions: message.reactions,
        });
      },
    };
  }, [
    currentUserId,
    sendbird.GroupChannel,
    getLatestUserMetaData,
    sendApiRequest,
    runCallback,
    setAnimatingCircleTarget,
    currentUser,
    scenario.channels,
  ]);
}
